import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const API_BASE         = process.env.REACT_APP_API_URL || '';

// ─────────────────────────────────────────────────────────────────────────────
// Translations for auth modal
// ─────────────────────────────────────────────────────────────────────────────

const AUTH_T = {
  en: {
    signIn:          'SIGN IN',
    register:        'REGISTER',
    createAccount:   'CREATE ACCOUNT',
    email:           'EMAIL',
    password:        'PASSWORD',
    or:              'OR',
    google:          'Continue with Google',
    processing:      'PROCESSING…',
    emailRequired:   'Email is required',
    invalidEmail:    'Invalid email format',
    passwordRequired:'Password is required',
    passwordMin:     'Password must be at least 6 characters',
  },
  es: {
    signIn:          'INICIAR SESIÓN',
    register:        'REGISTRARSE',
    createAccount:   'CREAR CUENTA',
    email:           'CORREO',
    password:        'CONTRASEÑA',
    or:              'O',
    google:          'Continuar con Google',
    processing:      'PROCESANDO…',
    emailRequired:   'El correo es obligatorio',
    invalidEmail:    'Formato de correo inválido',
    passwordRequired:'La contraseña es obligatoria',
    passwordMin:     'La contraseña debe tener al menos 6 caracteres',
  },
  he: {
    signIn:          'כניסה',
    register:        'הרשמה',
    createAccount:   'יצירת חשבון',
    email:           'אימייל',
    password:        'סיסמה',
    or:              'או',
    google:          'המשך עם Google',
    processing:      '…מעבד',
    emailRequired:   'אימייל נדרש',
    invalidEmail:    'פורמט אימייל לא תקין',
    passwordRequired:'סיסמה נדרשת',
    passwordMin:     'הסיסמה חייבת להכיל לפחות 6 תווים',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Google logo SVG
// ─────────────────────────────────────────────────────────────────────────────

function GoogleLogo({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────────────────────

const LABEL = {
  fontFamily: 'Share Tech Mono',
};

const INPUT_CLS =
  'w-full py-3 bg-white/5 border text-white text-xs tracking-wider ' +
  'placeholder-white/25 focus:outline-none transition-colors';

function Field({ icon: Icon, type, value, onChange, placeholder, autoComplete, action }) {
  return (
    <div className="relative">
      <Icon
        size={14}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00f0ff]/40 pointer-events-none"
      />
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={LABEL}
        className={`${INPUT_CLS} pl-9 ${action ? 'pr-10' : 'pr-4'} border-[#00f0ff]/15 focus:border-[#00f0ff]/50`}
      />
      {action && (
        <button
          type="button"
          onClick={action.fn}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
        >
          {action.icon}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function AuthModal({ isOpen, onClose }) {
  const { login }     = useAuth();
  const { lang }      = useLanguage();
  const labels        = AUTH_T[lang] || AUTH_T.en;

  const [mode,         setMode]         = useState('login');   // 'login' | 'register'
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);

  const hiddenGoogleRef = useRef(null);
  const googleLoadedRef = useRef(false);

  // ── Reset state on close ──────────────────────────────────
  const handleClose = useCallback(() => {
    setError('');
    setEmail('');
    setPassword('');
    setLoading(false);
    onClose();
  }, [onClose]);

  // ── Escape key ────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const fn = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [isOpen, handleClose]);

  // ── Google Identity Services ─────────────────────────────
  const initGoogle = useCallback(() => {
    if (!window.google || !hiddenGoogleRef.current || !GOOGLE_CLIENT_ID) return;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleCredential,
    });

    // Render Google button in hidden container (we use our own styled button)
    window.google.accounts.id.renderButton(hiddenGoogleRef.current, {
      theme: 'filled_black',
      size:  'large',
      type:  'icon',
      shape: 'rectangular',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen || !GOOGLE_CLIENT_ID) return;

    if (window.google) {
      initGoogle();
      return;
    }

    if (googleLoadedRef.current) return;
    googleLoadedRef.current = true;

    const script = document.createElement('script');
    script.src   = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.head.appendChild(script);
  }, [isOpen, initGoogle]);

  // Re-init when modal re-opens
  useEffect(() => {
    if (isOpen && window.google && GOOGLE_CLIENT_ID) {
      const t = setTimeout(initGoogle, 80);
      return () => clearTimeout(t);
    }
  }, [isOpen, initGoogle]);

  // ── Click hidden Google button ─────────────────────────────
  const handleGoogleClick = () => {
    if (!hiddenGoogleRef.current) return;
    const innerBtn =
      hiddenGoogleRef.current.querySelector('div[role="button"]') ||
      hiddenGoogleRef.current.querySelector('iframe') ||
      hiddenGoogleRef.current.querySelector('div');
    if (innerBtn) innerBtn.click();
  };

  // ── Google credential callback ────────────────────────────
  const handleGoogleCredential = async (response) => {
    setError('');
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/auth/google`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ credential: response.credential }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Google login failed');
      login(data.token, data.user);
      handleClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Email / password submit ───────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail)                                          return setError(labels.emailRequired);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail))     return setError(labels.invalidEmail);
    if (!password)                                             return setError(labels.passwordRequired);
    if (mode === 'register' && password.length < 6)            return setError(labels.passwordMin);

    setLoading(true);
    try {
      const endpoint = mode === 'register' ? '/api/auth/register' : '/api/auth/login';
      const res  = await fetch(`${API_BASE}${endpoint}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: trimmedEmail, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Authentication failed');
      login(data.token, data.user);
      handleClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // ── Render (portal to body so modal is never clipped by parent elements) ──
  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(10px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="relative w-full max-w-md"
        style={{
          background:  '#0a0a0f',
          border:      '1px solid rgba(57,255,20,0.22)',
          boxShadow:   '0 0 60px rgba(57,255,20,0.07), 0 0 120px rgba(0,0,0,0.9)',
        }}
      >
        {/* ── Top bar ──────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#39ff14]/10">
          <div>
            <p className="text-[10px] text-[#39ff14]/50 tracking-[0.35em] mb-1" style={LABEL}>
              // AUTH_SYSTEM
            </p>
            <h2
              className="text-xl font-bold text-white tracking-wider"
              style={{ fontFamily: 'Rajdhani' }}
            >
              {mode === 'login' ? labels.signIn : labels.createAccount}
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 text-white/30 hover:text-white/70 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          {/* ── Mode toggle ──────────────────────────────── */}
          <div className="flex border border-[#39ff14]/12" style={LABEL}>
            {['login', 'register'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setMode(m); setError(''); }}
                className={`flex-1 py-2 text-[11px] tracking-[0.22em] font-semibold transition-all duration-200 ${
                  mode === m
                    ? 'bg-[#39ff14]/8 text-[#39ff14] border-b-2 border-[#39ff14]'
                    : 'text-white/35 hover:text-white/55'
                }`}
              >
                {m === 'login' ? labels.signIn : labels.register}
              </button>
            ))}
          </div>

          {/* ── Form ─────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Field
              icon={Mail}
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={labels.email}
              autoComplete="email"
            />

            <Field
              icon={Lock}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={labels.password}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              action={{
                fn:   () => setShowPassword((v) => !v),
                icon: showPassword ? <EyeOff size={14} /> : <Eye size={14} />,
              }}
            />

            {/* Error message */}
            {error && (
              <div
                className="text-[11px] text-red-400/90 bg-red-500/8 border border-red-500/18 px-3 py-2 tracking-wide"
                style={LABEL}
              >
                ⚠&nbsp; {error}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 flex items-center justify-center gap-2 text-xs font-bold tracking-[0.3em] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                fontFamily:  'Rajdhani',
                background:  'rgba(57,255,20,0.08)',
                border:      '1px solid rgba(57,255,20,0.38)',
                color:       '#39ff14',
                boxShadow:   loading ? 'none' : '0 0 18px rgba(57,255,20,0.08)',
              }}
            >
              {loading ? (
                <><Loader2 size={14} className="animate-spin" /> {labels.processing}</>
              ) : (
                mode === 'login' ? labels.signIn : labels.createAccount
              )}
            </button>
          </form>

          {/* ── Divider + Google button ───────────────────── */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/8" />
                <span className="text-[10px] text-white/25 tracking-widest" style={LABEL}>
                  {labels.or}
                </span>
                <div className="flex-1 h-px bg-white/8" />
              </div>

              {/* Hidden real Google button */}
              <div
                ref={hiddenGoogleRef}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, overflow: 'hidden' }}
              />

              {/* Custom styled Google button with translated text */}
              <button
                type="button"
                onClick={handleGoogleClick}
                className="w-full py-3 flex items-center justify-center gap-3 text-sm font-medium transition-all duration-200 hover:bg-white/10"
                style={{
                  fontFamily:  'Rajdhani',
                  background:  'rgba(255,255,255,0.05)',
                  border:      '1px solid rgba(255,255,255,0.15)',
                  color:       '#ffffff',
                }}
              >
                <GoogleLogo size={18} />
                <span className="tracking-wide">{labels.google}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
